import collapse from "../assets/collapse.svg"
import expand from "../assets/expand-teams.svg"
import { useEffect, useState } from "react"
import Select from "react-select"
import countries from "i18n-iso-countries"
import enLocale from "i18n-iso-countries/langs/en.json"
import styles from "../utils/ReactSelectStyles"
import InfoRightPopUp from "./InfoRightPopUp"
import team_categories_api from "../api/team_categories"

countries.registerLocale(enLocale);

const EditTeamDetailsContainer = (props) => {
    const editor = props.editor
    const submit = props.submit

    const [teamName, setTeamName] = useState("")
    const [teamNameEdit, setTeamNameEdit] = useState("")
    const [description, setDescription] = useState("")
    const [descriptionEdit, setDescriptionEdit] = useState("")
    const [collapsed, setCollapsed] = useState(true)
    const [showNameError, setShowNameError] = useState(false)

    const [countryCodes, setCountryCodes] = useState([])
    const [countrySelected, setCountrySelected] = useState()
    const [showCountryReminder, setShowCountryReminder] = useState(false)
    const [categories, setCategories] = useState([])
    const noneCategory = { id: null, name: "None" }
    const [categorySelected, setCategorySelected] = useState(noneCategory)

    const desc_char_lim = 50 // Max 50 characters for the description

    useEffect(() => {
        setTeamName(props.name ?? "")
        setTeamNameEdit(props.name)
        setDescription(props.description ?? "")
        setDescriptionEdit(props.description ?? "")
        if (showNameError && (props.name ?? "").trim()) setShowNameError(false)

        // build country list once
        const items = Object.entries(
            countries.getNames("en", { select: "official" })
        ).map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        setCountryCodes(items)

        // preselect from props if exists
        if (props.country) {
            const found = items.find(c => c.code === props.country)
            if (found) setCountrySelected(found)
        }
    }, [props.name, props.description, props.country])

    useEffect(() => {
        team_categories_api.getTeamCategories().then(response => {
            setCategories(response.data.categories ?? [])
        })
    }, [])

    useEffect(() => {
        if (!props.categoryId) {
            setCategorySelected(noneCategory)
            return
        }
        const found = categories.find(c => c.id === props.categoryId)
        setCategorySelected(found ?? { id: props.categoryId, name: "HR" })
    }, [props.categoryId, categories])

    const updateTeamName = (ev) => {
        if (!ev) return
        const value = ev.target.value ?? ""
        setTeamNameEdit(value)
        if (showNameError && value.trim()) setShowNameError(false)
    }

    const updateDescription = (ev) => {
        if (!ev) return
        setDescriptionEdit(ev.target.value)
    }

    const handleCancelEdit = () => {
        setTeamNameEdit(teamName)
        setDescriptionEdit(description)
        setShowNameError(false)
        setCountrySelected(
            props.country
                ? countryCodes.find(c => c.code === props.country)
                : undefined
        )
        setCategorySelected(
            props.categoryId
                ? (categories.find(c => c.id === props.categoryId) ?? { id: props.categoryId, name: "HR" })
                : noneCategory
        )
        setShowCountryReminder(false)
    }

    const handleUpdate = () => {
        if (props.isDemo) return

        const nameTrimmed = (teamNameEdit ?? "").trim()
        if (!nameTrimmed) {
            setShowNameError(true)
            return
        }

        if (!countrySelected || !countrySelected.code) {
            setShowCountryReminder(true)
            return
        }

        submit({
            name: nameTrimmed,
            description: descriptionEdit,
            country: countrySelected.code,
            category_id: categorySelected?.id ?? null,
        })
    }

    const handleCollapse = () => {
        setCollapsed(!collapsed)
    }

    const countryPlaceholder =
        !props.country ? "Country is now mandatory" : ""

    return (
        <div className={`edit-team-details-container ${collapsed ? "collapsed" : ""}`}>
            <div className={`edit-team-details-top-row ${collapsed ? "collapsed" : ""}`}>
                <div className="edit-team-details-title bold">Team Details</div>
                <div className={`edit-team-details-collapse ${collapsed ? "collapsed" : ""}`} onClick={handleCollapse}>
                    <img src={collapsed ? expand : collapse} alt={collapsed ? "Show team details" : "Hide team details"} />
                </div>
            </div>
            {!collapsed ?
                <>
                    <div className="edit-team-details-middle-row">
                        <div className="edit-team-details-middle-row-section">
                            <div className="edit-team-details-subheading medium">
                                Team Name <InfoRightPopUp text={"This information will appear in the survey invitations emails and reports."} />
                            </div>
                            <div className="edit-team-details-input regular">
                                <input
                                    type="text"
                                    value={teamNameEdit}
                                    onChange={updateTeamName}
                                    disabled={!editor || props.isDemo}
                                    aria-invalid={showNameError ? "true" : "false"}
                                    aria-describedby={showNameError ? "edit-team-name-error" : undefined}
                                />
                                {showNameError && (
                                    <div
                                        id="edit-team-name-error"
                                        className="field-error"
                                        style={{ color: "#cc0011", fontStyle: "italic", marginTop: 4 }}
                                    >
                                        Team Name is mandatory
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="edit-team-details-middle-row-section">
                            <div className="edit-team-details-subheading medium">
                                Team Description <InfoRightPopUp text={"Short description for your reference only (max 50 characters)"} />
                            </div>
                            <div className="edit-team-details-textarea regular">
                                <textarea value={descriptionEdit} maxLength={desc_char_lim} onChange={updateDescription} disabled={!editor || props.isDemo} />
                                {!props.isDemo?
                                    <div
                                        className="help-text"
                                        style={{
                                            marginTop: 4,
                                            fontSize: 12,
                                            fontStyle: "italic",
                                            color: descriptionEdit.length >= desc_char_lim ? "#cc0011" : "#666"
                                        }}
                                    >
                                        {descriptionEdit.length}/{desc_char_lim} characters
                                    </div>
                                    : null
                                }
                            </div>
                        </div>

                        <div className="edit-team-details-middle-row-section">
                            <div className="edit-team-details-subheading medium">
                                Team Location <InfoRightPopUp text={"Please select the country where the team members are predominantly based. This is used to support benchmarking analysis."} />
                            </div>
                            <div className="edit-team-details-input regular">
                                <Select
                                    name="traits.country"
                                    options={countryCodes}
                                    getOptionLabel={option => option.name}
                                    getOptionValue={option => option.code}
                                    value={countrySelected}
                                    placeholder={countryPlaceholder}
                                    onChange={opt => {
                                        setCountrySelected(opt ?? null)
                                        setShowCountryReminder(false)
                                    }}
                                    onBlur={() => {
                                        if (!countrySelected?.code) setShowCountryReminder(true)
                                    }}
                                    styles={styles.registerStyles}
                                    isDisabled={!editor || props.isDemo}
                                />
                                {showCountryReminder && (
                                    <div className="messages">This field is required.</div>
                                )}
                            </div>
                        </div>

                        <div className="edit-team-details-middle-row-section">
                            <div className="edit-team-details-subheading medium">
                                Team Category <InfoRightPopUp text={"Optional. Used to compare this team against a matching benchmark overlay, such as HR."} />
                            </div>
                            <div className="edit-team-details-input regular">
                                <Select
                                    name="team.category"
                                    options={[noneCategory, ...categories]}
                                    getOptionLabel={option => option.name}
                                    getOptionValue={option => option.id ?? ""}
                                    value={categorySelected}
                                    placeholder=""
                                    onChange={opt => setCategorySelected(opt ?? noneCategory)}
                                    styles={styles.registerStyles}
                                    isDisabled={!editor || props.isDemo}
                                />
                            </div>
                        </div>

                    </div>

                    <div className="edit-team-details-bottom-row" style={{ marginTop: "16px", display: props.isDemo || !editor ? "none" : "flex" }}>
                        <div className="edit-team-details-button bold" onClick={handleCancelEdit}>CANCEL</div>
                        <div
                            className={`edit-team-details-button bold fill ${(!countrySelected?.code || !teamNameEdit.trim()) ? "disabled" : ""}`}
                            onClick={handleUpdate}
                        >
                            UPDATE
                        </div>
                    </div>
                </>
                : null
            }
        </div>
    )
}

export default EditTeamDetailsContainer
